import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, WHATSAPP_URL, SERVICES } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", phone: "", email: "", service: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/40 px-5 py-4 text-sm text-white placeholder:text-white/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/25";

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Bitte wählen Sie eine Leistung aus.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet – wir melden uns schnellstmöglich.");
      setForm(initial);
    } catch {
      toast.error(`Senden fehlgeschlagen. Bitte rufen Sie uns direkt an: ${PHONE_DISPLAY}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative bg-ink py-24 md:py-36 overflow-hidden">
      <div className="absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-brand/10 blur-[140px]" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="mb-4 text-xs font-bold tracking-[0.3em] text-brand-bright">KONTAKT</p>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white leading-[1.02]">
            Sie benötigen<br />
            <span className="text-brand-bright">unsere Unterstützung?</span>
          </h2>
          <p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-white/60">
            Kontaktieren Sie uns für ein schnelles & unverbindliches Angebot – telefonisch,
            per E-Mail oder über das Formular.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-phone-link"
              href={PHONE_TEL}
              className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-surface p-5 transition-[border-color,transform] duration-300 hover:border-brand/50 hover:-translate-y-0.5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand-bright ring-1 ring-brand/30 transition-colors duration-300 group-hover:bg-brand group-hover:text-black">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[11px] font-bold tracking-[0.2em] text-white/40">TELEFON – 24/7 ERREICHBAR</span>
                <span className="block text-lg font-extrabold text-white">{PHONE_DISPLAY}</span>
              </span>
            </a>
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-surface p-5 transition-[border-color,transform] duration-300 hover:border-brand/50 hover:-translate-y-0.5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand-bright ring-1 ring-brand/30 transition-colors duration-300 group-hover:bg-brand group-hover:text-black">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[11px] font-bold tracking-[0.2em] text-white/40">E-MAIL</span>
                <span className="block text-lg font-extrabold text-white break-all">{EMAIL}</span>
              </span>
            </a>
            <div className="flex items-center gap-5 rounded-2xl border border-white/10 bg-surface p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand-bright ring-1 ring-brand/30">
                <MapPin className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-[11px] font-bold tracking-[0.2em] text-white/40">EINSATZGEBIET</span>
                <span className="block text-sm font-bold text-white">
                  Gronau (Westf.) · Epe · Ochtrup · Ahaus · Enschede & Umgebung
                </span>
              </span>
            </div>
            <a
              data-testid="contact-whatsapp-link"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-brand-bright hover:text-white transition-colors duration-300 pl-1"
            >
              ODER DIREKT PER WHATSAPP SCHREIBEN
            </a>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-10"
        >
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
            Angebot anfragen
          </h3>
          <p className="mt-2 text-sm text-white/50">Unverbindlich & kostenfrei – Antwort in der Regel am selben Tag.</p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              data-testid="contact-form-name"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Ihr Name *"
              aria-label="Name"
              className={inputCls}
            />
            <input
              data-testid="contact-form-phone"
              required
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="Telefonnummer *"
              aria-label="Telefonnummer"
              className={inputCls}
            />
            <input
              data-testid="contact-form-email"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="E-Mail-Adresse *"
              aria-label="E-Mail-Adresse"
              className={`${inputCls} sm:col-span-2`}
            />
            <select
              data-testid="contact-form-service"
              required
              value={form.service}
              onChange={set("service")}
              aria-label="Gewünschte Leistung"
              className={`${inputCls} sm:col-span-2 appearance-none ${form.service ? "text-white" : "text-white/35"}`}
            >
              <option value="" disabled>
                Gewünschte Leistung wählen *
              </option>
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-surface text-white">
                  {s}
                </option>
              ))}
            </select>
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Ihre Nachricht – worum geht es? *"
              aria-label="Nachricht"
              className={`${inputCls} sm:col-span-2 resize-none`}
            />
          </div>

          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={sending}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-extrabold tracking-wider text-black transition-colors duration-300 hover:bg-brand-bright disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "WIRD GESENDET…" : "UNVERBINDLICHES ANGEBOT ANFRAGEN"}
          </button>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-white/35">
            Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur Bearbeitung der Anfrage zu.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
